import React, { useMemo, useRef } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import Export from 'src/components/Export/ExportCSV';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  parseBeaconBuyBoxSellersResponse,
  useBeaconBuyBoxSellersData
} from 'src/components/BeaconV2Components/ExperimentalLayout/BuyBox/Sellers/useBeaconBuyBoxSellersData';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import BuyBoxLossRateByProductGrid from './BuyBoxLossRateByProductGrid';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { Box } from '@mui/system';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import BuyBoxByWeekGrid from 'src/components/BeaconV2Components/ExperimentalLayout/BuyBox/Summary/BuyBoxByWeekGrid';

const BuyBoxSellersPage = () => {
  const { isLoading, data } = useBeaconBuyBoxSellersData();
  const entityType = useMainEntityType();

  const sellerBuyBoxData = useMemo(() => {
    return data ? parseBeaconBuyBoxSellersResponse(data) : [];
  }, [data]);

  const theme = useStacklineTheme();
  const saveImageRef = useRef<HTMLDivElement>(null);

  return (
    <BasePageContainer>
      <Flex gap={BEACON_CHART_SPACING} flexDirection="column">
        {isLoading ? (
          <SplineChartLoading width={900} />
        ) : (
            <Flex flexDirection="column">
              <Box
                sx={{
                  marginBottom: theme.spacing.lg,
                  flexDirection: 'row',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <CommonSummaryInfoSubtitle title="Sellers" />
                </Box>
                <Export
                  title="Buy Box Sellers"
                  saveImageRef={saveImageRef}
                  convertSeriesToDelimitedData={async () => {
                    saveSingleBarChartAsDelimitedData(sellerBuyBoxData, {
                      fileTitle: `BuyBoxSellers-${entityType}`
                    });
                  }}
                />
              </Box>
              <div ref={saveImageRef}>
                <TopEntityChartV2
                  chartSeries={[
                    {
                      type: 'column',
                      data: sellerBuyBoxData,
                      metricType: METRICTYPE.PERCENT
                    }
                  ]}
                  seriesColors={[theme.colors.primary]}
                  chartOptionsOverride={{
                    chart: {
                      height: 300
                    }
                  }}
                />
              </div>
            </Flex>
          )}
        {entityType !== 'product' && <BuyBoxLossRateByProductGrid />}
        {entityType === 'product' && (
          <BuyBoxByWeekGrid
            gridTitle="Loss Rate by Week"
            fields={[
              {
                name: 'buyBoxWeighted',
                displayInTable: false,
                comparisonFormatter: () => null
              },
              {
                name: 'winsByClient',
                displayInTable: false,
                comparisonFormatter: () => null
              },
              {
                name: 'lossPercentage',
                displayName: 'Buy Box - Loss Rate',
                comparisonFormatter: () => null
              },
              {
                name: 'winPercentage',
                displayName: 'Buy Box - Win Rate',
                comparisonFormatter: () => null
              }
            ]}
          />
        )}
      </Flex>
    </BasePageContainer>
  );
};

export default BuyBoxSellersPage;
